import React from 'react';

interface ToggleProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  showLabels?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  'aria-label'?: string;
}

const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  showLabels = false,
  size = 'md',
  className = '',
  'aria-label': ariaLabel,
}) => {
  const handleToggle = () => {
    if (!disabled) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      handleToggle();
    }
  };

  // サイズ別のスタイル設定（取引先選択デザインに統一）
  const sizeClasses = {
    sm: {
      track: 'w-8 h-4',
      thumb: 'w-3 h-3',
      translate: 'translate-x-4',
      translateInitial: 'translate-x-0.5',
    },
    md: {
      track: 'w-11 h-6',
      thumb: 'w-5 h-5',
      translate: 'translate-x-5',
      translateInitial: 'translate-x-0.5',
    },
    lg: {
      track: 'w-14 h-7',
      thumb: 'w-6 h-6',
      translate: 'translate-x-7',
      translateInitial: 'translate-x-0.5',
    },
  };

  const currentSize = sizeClasses[size];

  return (
    <div className={`flex items-center ${className}`}>
      {label && !showLabels && (
        <span className='mr-3 text-sm font-medium text-gray-900 dark:text-white'>{label}</span>
      )}

      <div className='flex items-center'>
        {showLabels && (
          <span className='mr-2 text-xs font-medium text-gray-500 dark:text-gray-400'>無効</span>
        )}

        <button
          type='button'
          role='switch'
          aria-checked={checked}
          disabled={disabled}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          aria-label={ariaLabel || label}
          className={`relative inline-flex items-center ${currentSize.track} rounded-full transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 border-2 ${
            disabled
              ? 'cursor-not-allowed opacity-50 bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
              : checked
                ? 'cursor-pointer bg-primary-600 dark:bg-primary-500 border-primary-700 dark:border-primary-400'
                : 'cursor-pointer bg-gray-100 dark:bg-gray-800 border-gray-400 dark:border-gray-500'
          }`}
        >
          <span
            className={`inline-block ${currentSize.thumb} bg-white dark:bg-gray-100 rounded-full shadow-md transform transition-transform duration-200 ease-in-out ${
              checked ? currentSize.translate : currentSize.translateInitial
            }`}
          />
        </button>

        {showLabels && (
          <span className='ml-2 text-xs font-medium text-gray-500 dark:text-gray-400'>有効</span>
        )}
      </div>

      {label && showLabels && (
        <span className='ml-3 text-sm font-medium text-gray-900 dark:text-white'>{label}</span>
      )}
    </div>
  );
};

export default Toggle;
